import ChargeHelper, { ChargeConstants } from 'features/Charge/chargeHelper';
import { AppCustomDialogHelper } from 'general/components/AppDialogCustom';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import AppColors from 'general/constants/AppColors';
import UIHelper from 'general/helpers/UIHelper';
import useRouter from 'hooks/useRouter';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import QRCode from 'react-qr-code';
import { useSelector } from 'react-redux';
import DialogAddChargeConnectorView from '../DialogAddChargeConnectorView';

ChargeConnectorListView.propTypes = {
  chargePointDetail: PropTypes.object,
  items: PropTypes.array,
  onRefresh: PropTypes.func,
  hasAppTab: PropTypes.bool,
};

function ChargeConnectorListView({ chargePointDetail, items, onRefresh, hasAppTab = false }) {
  // MARK: ---- PROPERTIES ----
  const router = useRouter();
  const { user } = useSelector((state) => state.auth);
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'OPERATOR';
  const chargeConnectorColumns = useMemo(() => {
    return [
      {
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Mã QR
          </span>
        ),
        selector: (row) => (
          <div
            className="p-2 cursor-pointer hover-opacity-60"
            onClick={() => {
              handleShowQRCode(row);
            }}
          >
            <QRCode value={ChargeHelper.Utils.getChargeConnectorQRCodeLink(row)} size={34} />
          </div>
        ),
        width: '70px',
        center: true,
        style: tableStyle.columns.normal,
      },
      {
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Súng sạc
          </span>
        ),
        selector: (row) => {
          return `Súng sạc ${row?.connectorNumber}`;
        },
        minWidth: '100px',
        style: tableStyle.columns.normal,
      },
      {
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Mã súng sạc
          </span>
        ),
        selector: (row) => row?.connectorCode,
        minWidth: '120px',
        style: tableStyle.columns.normal,
      },
      {
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Loại súng sạc
          </span>
        ),
        selector: (row) => row?.type?.length ? ChargeHelper?.Utils?.getChargeConnectorTypeName(row?.type) : '',
        minWidth: '160px',
        style: tableStyle.columns.normal,
      },
      {
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Serial
          </span>
        ),
        selector: (row) => row?.connectorSerial ?? 'Chưa thiết lập',
        minWidth: '140px',
        style: tableStyle.columns.normal,
      },
      {
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Trạng thái
          </span>
        ),
        selector: (row) => {
          return (
            <KTLabel
              text={ChargeHelper.Utils.getChargeConnectorStatusName(row?.status)}
              isInline={true}
              size={KTLabelSize.medium}
              additionalClassName={`label-light-${ChargeHelper.Utils.getChargeConnectorStatusColor(
                row?.status
              )}`}
            />
          );
        },
        minWidth: '160px',
        style: tableStyle.columns.normal,
      },
      {
        name: (
          <div className="d-flex flex-column align-items-end">
            <span
              className="letter-spacing-unset"
              style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
            >
              Tổng công suất hiện tại
            </span>
            <span
              className="letter-spacing-unset"
              style={UIHelper.buildFontStyle('500', '12px', AppColors.gray[700])}
            >
              kW
            </span>
          </div>
        ),
        selector: (row) =>
          row?.currentTotalPower ? (row?.currentTotalPower / 1000).toFixed(2) : '',
        minWidth: '180px',
        right: true,
        style: tableStyle.columns.normal,
      },
      {
        name: (
          <div className="d-flex flex-column align-items-end">
            <span
              className="letter-spacing-unset"
              style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
            >
              Nhiệt độ
            </span>
            <span
              className="letter-spacing-unset"
              style={UIHelper.buildFontStyle('500', '12px', AppColors.gray[700])}
            >
              °C
            </span>
          </div>
        ),
        selector: (row) => row?.temperature,
        minWidth: '120px',
        right: true,
        style: tableStyle.columns.normal,
      },
      {
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Thao tác
          </span>
        ),
        width: '140px',
        style: {
          position: 'sticky',
          right: 0,
          backgroundColor: '#fff',
          borderLeft: '1px solid #ebedfe',
        },
        cell: (row) => {
          return UIHelper.buildMoreActionsButton(buildMoreActions(row), true);
        },
      },
    ];
  }, [user, chargePointDetail]);

  // MARK: ---- METHODS ----
  const buildMoreActions = (row) => {
    const actions = [];
    if (isAdmin) {
      // chinh sua
      actions.push({
        text: 'Chỉnh sửa',
        iconElement: <i className="fa-regular fa-edit" style={{ fontSize: '1.1rem' }} />,
        click: () => {
          handleAddChargeConnector(row);
        },
      });
      // kich hoat
      if (row?.status === ChargeConstants.ChargeConnectorStatus.AVAILABLE) {
        actions.push({
          text: 'Kích hoạt súng sạc',
          iconElement: <i className="fa-regular fa-bolt" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            handleActiveCharge(row);
          },
        });
      }
      // xoa
      actions.push({
        text: 'Xoá súng sạc',
        iconElement: <i className="fa-regular fa-trash-can" style={{ fontSize: '1.1rem' }} />,
        click: () => {
          handleDeleteChargeConnector(row);
        },
      });
    }
    return actions;
  };

  const handleStopCharge = (row) => {
    ChargeHelper.UI.showDialogConfirmStopChargeConnector(row, () => {
      // dung sac thanh cong
      onRefresh && onRefresh();
    });
  };
  const handleActiveCharge = (row) => {
    ChargeHelper.UI.showDialogConfirmActivateChargeConnector(
      chargePointDetail?.pricing,
      row,
      () => {
        AppCustomDialogHelper.hide();
        if (onRefresh) {
          onRefresh();
        }
      }
    );
  };
  const handleDeleteChargeConnector = (row) => {
    ChargeHelper.UI.showDialogConfirmDeleteChargeConnector(row, () => {
      // xoa voi sac thanh cong
      onRefresh && onRefresh();
    });
  };
  const handleAddChargeConnector = (row) => {
    AppCustomDialogHelper.show(
      row ? 'Chỉnh sửa súng sạc' : 'Thêm súng sạc',
      <DialogAddChargeConnectorView
        chargePointDetail={chargePointDetail}
        item={row}
        onCancel={() => {
          AppCustomDialogHelper.hide();
        }}
        onDone={() => {
          AppCustomDialogHelper.hide();
          onRefresh && onRefresh();
        }}
      />,
      'md',
      false,
      true
    );
  };
  const handleShowQRCode = (row) => {
    AppCustomDialogHelper.show(
      `Súng sạc ${row?.connectorNumber}`,
      <div className="p-4">
        <QRCode value={ChargeHelper.Utils.getChargeConnectorQRCodeLink(row)} size={'100%'} />
      </div>,
      'md',
      true,
      true
    );
  };

  return (
    <div className="px-4">
      {UIHelper.buildCard(
        <div className="pb-5">
          {/* header */}
          <div className="px-5 pt-5 pb-3 d-flex align-items-center justify-content-between">
            <div className="d-flex flex-column">
              <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                Danh sách súng sạc
              </span>
              <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                Tổng:{' '}
                <span style={UIHelper.buildFontStyle('600', '14px', AppColors.primary)}>
                  {items?.length} súng sạc
                </span>
              </span>
            </div>
            <div>
              {/* Chỗ sửa số lượng súng sạc tối đa trong 1 trụ <3 */}
              {isAdmin &&
                items?.length < 2 &&
                UIHelper.buildGreenButton(
                  'Thêm súng sạc',
                  () => {
                    handleAddChargeConnector();
                  },
                  <i className="fa-regular fa-circle-plus" />
                )}
            </div>
          </div>

          {/* table */}
          <div className="px-5">
            <KTTable
              columns={chargeConnectorColumns}
              items={items}
              selectableRows={false}
              striped={false}
              compactGrid={true}
              onClickedRow={(row) => { }}
              onSelectedRowsChanged={(rows) => { }}
              onDoubleClickedRow={(row) => { }}
              noDataComponent={UIHelper.buildEmptyNoData(
                'Trụ sạc này chưa có súng sạc nào được kết nối.'
              )}
            />
          </div>
        </div>,
        false,
        false,
        true,
        false,
        AppColors.gray[300],
        hasAppTab
      )}
    </div>
  );
}

export default ChargeConnectorListView;
